import React from 'react';
import PropTypes from 'prop-types';
import {Toggle} from '@/components/atoms';
import {
  REWARD_TYPES,
  isInventoryManagedRewardType
} from '@/utils/constants/rewardsConstants';
import {
  displayOutOfStockSchema,
  inStockSchema,
  unlimitedInventorySchema
} from '../schemas';

/**
 * ToggleRow component - Reusable wrapper for all toggle switches in a row
 * Conditionally shows unlimited inventory toggle for Cash/Product/Physical reward types
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing displayOutOfStock, inStock, unlimitedInventory, showOnHomePage, rewardType
 * @param {Function} props.handleDisplayOutOfStockChange - Function to handle form field changes
 * @param {Function} props.handleUnlimitedInventoryChange - Function to handle form field changes
 * @param {Function} props.handleInStockChange - Function to handle form field changes
 * @param {Function} props.handleShowOnHomePageChange - Function to handle showOnHomePage toggle changes
 * @returns {JSX.Element} The rendered ToggleRow component
 */
const ToggleRow = ({
  formData,
  handleDisplayOutOfStockChange,
  handleUnlimitedInventoryChange,
  handleInStockChange,
  handleShowOnHomePageChange
}) => {
  // Check if reward type supports inventory to show unlimited inventory toggle.
  // Guard against undefined so we don't accidentally match mocked undefined constants.
  const inventoryRewardType = formData?.rewardType;
  const showUnlimitedInventory =
    !!inventoryRewardType &&
    // Preserve previous behavior: VISA_CARD always shows unlimited inventory toggle
    (inventoryRewardType === REWARD_TYPES.VISA_CARD ||
      isInventoryManagedRewardType(inventoryRewardType, formData?.cardType));

  return (
    <>
      <div className='flex flex-col items-start gap-6 md:flex-row'>
        {/* Unlimited Inventory Toggle - Only shown for Cash/Product/Physical/VISA_CARD/DIGITAL/DIGITAL_PRE_ORDER/LOYALTY/PEP_VOUCHER_DISCOUNT */}
        {showUnlimitedInventory && (
          <div className='mr-[70px] w-[194px]'>
            <Toggle
              value={formData?.unlimitedInventory || false}
              updateValue={handleUnlimitedInventoryChange}
              uiSchema={unlimitedInventorySchema}
              id='unlimited_inventory'
            />
          </div>
        )}

        {/* Display Out of Stock Toggle */}
        <div className='mr-[70px] w-[194px]'>
          <Toggle
            value={formData?.displayOutOfStock || false}
            updateValue={handleDisplayOutOfStockChange}
            uiSchema={displayOutOfStockSchema}
            id='display_out_of_stock'
          />
        </div>

        {/* In Stock Toggle */}
        <div className='mr-[70px] w-[194px]'>
          <Toggle
            value={formData?.inStock || false}
            updateValue={handleInStockChange}
            uiSchema={inStockSchema}
            id='in_stock'
          />
        </div>
      </div>

      {/* Show on Homepage Toggle - Shown for all reward types on next line */}
      <div className='mt-6 flex flex-col items-start gap-6 md:flex-row'>
        <div className='mr-[70px] w-[194px]'>
          <Toggle
            value={formData?.showOnHomePage || false}
            updateValue={handleShowOnHomePageChange}
            uiSchema={{
              label: 'Shown on Homepage',
              size: 'medium',
              variant: 'horizontal',
              isDark: true,
              className: ''
            }}
            id='show_on_homepage'
          />
        </div>
      </div>
    </>
  );
};

ToggleRow.propTypes = {
  formData: PropTypes.shape({
    displayOutOfStock: PropTypes.bool,
    inStock: PropTypes.bool,
    unlimitedInventory: PropTypes.bool,
    showOnHomePage: PropTypes.bool,
    rewardType: PropTypes.string,
    cardType: PropTypes.string
  }).isRequired,
  handleDisplayOutOfStockChange: PropTypes.func.isRequired,
  handleUnlimitedInventoryChange: PropTypes.func.isRequired,
  handleInStockChange: PropTypes.func.isRequired,
  handleShowOnHomePageChange: PropTypes.func.isRequired
};

export default ToggleRow;
